import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Row, SortingRule } from 'react-table';
import { IFetchDataParams, ITableToolbarOptionsProps, IBulkOptionsProps, IRowSelectionParams, IDataTableProps } from '@amobee/component-library';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { Subscription, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { FilterSetup } from './list-filter/list-filter.component';
import { intersection } from 'lodash';

export interface ListResponse {
  results: Array<any>;
  count?: number;
}

export interface TableConfigs extends IDataTableProps {
  endpoint?: string;
  fetchStaticData?(params: object): Promise<ListResponse>
  [props: string]: any;
}

export interface BulkOptionsProps extends IBulkOptionsProps {
  /* These method will be implemented by data table component to pass down selected rows
  onBulkEditClick?(): void;
  onBulkCopyClick?(): void;
  onBulkStartClick?(): void;
  onBulkPauseClick?(): void;
  onBulkRemoveClick?(): void;
  */
  onBulkEditClickExec?(selectedRows: Array<Row>): void;
  onBulkCopyClickExec?(selectedRows: Array<Row>): void;
  onBulkStartClickExec?(selectedRows: Array<Row>): void;
  onBulkPauseClickExec?(selectedRows: Array<Row>): void;
  onBulkRemoveClickExec?(selectedRows: Array<Row>): void;
}

export interface ToolBarConfigs {
  toolbarOptions?: ITableToolbarOptionsProps,
  bulkOptions?: BulkOptionsProps,
  isSearchEnable?: boolean,
  filterSetup?: FilterSetup[]
}

@Component({
  selector: 'ccp-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnInit, OnDestroy, OnChanges {

  @Input() public tableConfigs: TableConfigs;
  @Input() public columns: any[];
  @Input() public toolbarConfigs: ToolBarConfigs;
  // if this is set to any new reference in parent component, it will refresh the table
  @Input() public reloadTableTriggered?: any;
  // parent component can change filter by providing a new reference that filter will be extended
  @Input() public set filterUpdates(value: object) {
    if (value) {
      Object.assign(this.filters, value);
      // update preSelectedFilters here to trigger view update
      this.preSelectedFilters = Object.assign({}, this.preSelectedFilters, this.filters);
      this.handleOnFilterChanged(this.filters);
    }
  }
  @Output() public onRowSelectionChange = new EventEmitter<Array<any>>();
  public data: any[] = [];
  public selectedRows: Row[] = [];
  private pageCount: number = 0; // reset after data fetched
  private totalRows: number = 0; // reset after data fetched
  public isTableReady: boolean = false;
  private currentPageIndex: number = 0;
  private currentSorting: SortingRule<object>;
  private filters: any = {};
  public preSelectedFilters: any = {};
  private searchString: String = '';
  private queryParams: ParamMap;
  private queryParamsSubcrption: Subscription;

  constructor(private router: Router, private route: ActivatedRoute, private http: HttpClient) { }

  ngOnInit() {
    if (this.toolbarConfigs && this.toolbarConfigs.bulkOptions) {
      if (this.toolbarConfigs.bulkOptions.onBulkEditClickExec) {
        this.toolbarConfigs.bulkOptions.onBulkEditClick = () => {
          this.toolbarConfigs.bulkOptions.onBulkEditClickExec(this.selectedRows);
        };
      }
      if (this.toolbarConfigs.bulkOptions.onBulkCopyClickExec) {
        this.toolbarConfigs.bulkOptions.onBulkCopyClick = () => {
          this.toolbarConfigs.bulkOptions.onBulkCopyClickExec(this.selectedRows);
        };
      }
      if (this.toolbarConfigs.bulkOptions.onBulkStartClickExec) {
        this.toolbarConfigs.bulkOptions.onBulkStartClick = () => {
          this.toolbarConfigs.bulkOptions.onBulkStartClickExec(this.selectedRows);
        };
      }
      if (this.toolbarConfigs.bulkOptions.onBulkPauseClickExec) {
        this.toolbarConfigs.bulkOptions.onBulkPauseClick = () => {
          this.toolbarConfigs.bulkOptions.onBulkPauseClickExec(this.selectedRows);
        };
      }
      if (this.toolbarConfigs.bulkOptions.onBulkRemoveClickExec) {
        this.toolbarConfigs.bulkOptions.onBulkRemoveClick = () => {
          this.toolbarConfigs.bulkOptions.onBulkRemoveClickExec(this.selectedRows);
        };
      }
    }
    this.currentSorting = Object.assign({}, this.tableConfigs.defaultSort);
    this.queryParamsSubcrption = this.route.queryParamMap.subscribe(
      (queryParams: ParamMap) => {
        if (this.isTableReady && !intersection(queryParams.keys, Object.keys(this.filters)).length) {
          // if all query params from filterSetup are cleared and there are still filters applying in table, we need to reset the filters
          this.filters = {};
          this.preSelectedFilters = {};
        }
        this.queryParams = queryParams;
        this.reloadTable();
      }
    );
  }

  ngOnDestroy(): void {
    if (this.queryParamsSubcrption) {
      this.queryParamsSubcrption.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reloadTableTriggered && changes.reloadTableTriggered.currentValue) {
      this.selectedRows.length = 0;
      // set table ready to false so that filter will be mapped into query params
      this.isTableReady = false;
      this.reloadTable();
    }
  }

  reloadTable(appendData: boolean = false) {
    let httpParams: any = {};
    for (const key of this.route.snapshot.paramMap.keys) {
      httpParams[key] = this.route.snapshot.paramMap.get(key);
    }
    for (const key of this.queryParams.keys) {
      if (!this.isTableReady && this.toolbarConfigs && this.toolbarConfigs.filterSetup) {
        for (const filter of this.toolbarConfigs.filterSetup) {
          if (key === filter.name) {
            if (this.queryParams.get(key) !== '' && !isNaN(Number(this.queryParams.get(key)))) {
              this.filters[key] = Number(this.queryParams.get(key));
            } else {
              this.filters[key] = this.queryParams.get(key);
            }
            break;
          }
        }
      }
      httpParams[key] = this.queryParams.get(key);
    }
    if (!this.isTableReady) {
      this.preSelectedFilters = Object.assign({}, this.filters);
    }
    Object.assign(httpParams, this.filters);
    if (this.searchString && this.searchString !== '') {
      httpParams.searchString = this.searchString;
    }

    if (!this.tableConfigs.disableSorting && this.currentSorting.id) {
      httpParams.columnKey = this.currentSorting.id;
      httpParams.sortOrder = this.currentSorting.desc ? 'desc' : 'asc';
      if (this.tableConfigs.defaultSort && (this.tableConfigs.defaultSort.id !== this.currentSorting.id || this.tableConfigs.defaultSort.desc !== this.currentSorting.desc)) {
        // overwrite default sort
        this.tableConfigs.defaultSort = Object.assign({}, this.currentSorting);
      }
    }

    if (this.tableConfigs.isPaginationEnabled) {
      if (!appendData) {
        this.currentPageIndex = 0;
      }
      httpParams.offset = this.currentPageIndex * this.tableConfigs.pageSize;
      httpParams.limit = this.tableConfigs.pageSize;
    }
    this.isTableReady = false;
    if (this.tableConfigs.endpoint) {
      this.http.get(this.tableConfigs.endpoint, { params: httpParams }).pipe(
        catchError(() => of({
          results: [],
          count: 0
        }))
      ).toPromise().then(
        (response: ListResponse) => {
          this.processData(response, appendData);
        }
      );
    } else if (this.tableConfigs.fetchStaticData) {
      this.tableConfigs.fetchStaticData(httpParams).then(
        (response: ListResponse) => {
          this.processData(response, appendData);
        }
      )
    }
  }

  processData(response: ListResponse, appendData: boolean) {
    if (!appendData) {
      this.totalRows = response.count || response.results.length;
      this.pageCount = Math.ceil(this.totalRows / this.tableConfigs.pageSize);
      this.data = [...response.results];
      this.isTableReady = true;
      this.reloadTableTriggered = undefined;
    } else {
      this.data = [...this.data, ...response.results];
      this.isTableReady = true;
    }
  }

  handleOnSearchTextChange(searchString: String) {
    // acl-ngx-input will have distinct value check before emiting changes, safe to call the endpoint here
    if (searchString !== this.searchString) {
      this.searchString = searchString;
      this.reloadTable();
    }
  }

  handleOnFilterSelected(filterSetup: FilterSetup) {
    // check undefined in order to avoid case for 0 or -1
    if (this.filters[filterSetup.name] == undefined) {
      this.filters[filterSetup.name] = filterSetup.initialValue;
      // update preSelectedFilters here to trigger view update
      this.preSelectedFilters = Object.assign({}, this.filters);
      this.handleOnFilterChanged(this.filters);
    }
  }

  handleOnFilterChanged(filters: object) {
    this.filters = Object.assign({}, filters);
    let queryParams = Object.assign({}, filters);
    if (this.toolbarConfigs && this.toolbarConfigs.filterSetup) {
      this.toolbarConfigs.filterSetup.forEach((value) => {
        if (queryParams[value.name] == undefined) {
          queryParams[value.name] = null;
        }
      });
    }
    this.router.navigate([], { relativeTo: this.route, queryParams: queryParams, queryParamsHandling: 'merge', replaceUrl: true });
  }

  handleOnRowSelectionChange(rowSelection: IRowSelectionParams) {
    this.selectedRows = [...rowSelection.selectedVisibleRows];
    this.onRowSelectionChange.emit(rowSelection.selectedVisibleRows);
  }

  handleOnFetchData(event: IFetchDataParams) {
    if (event.sortBy && event.sortBy.length > 0 && (event.sortBy[0].id !== this.currentSorting.id || event.sortBy[0].desc !== this.currentSorting.desc)) {
      Object.assign(this.currentSorting, event.sortBy[0]);
      // need to reoload table in this case to reset page index
      this.reloadTable();
    } else if (event.pageIndex - this.currentPageIndex === 1) {
      this.currentPageIndex = event.pageIndex;
      // if sorting is the same and it is next page request, we can just append the list
      this.reloadTable(true);
    }
  }

}

